import { createFileRoute, Link } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useAccountsTree, useCreateAccount, useUpdateAccount, useDeactivateAccount } from '../../../features/accounting/client';
import type { AccountNode as AccountNodeData } from '../../../features/accounting/schemas';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Input, FormField } from '../../../components/ui/FormField';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useToast } from '../../../components/ui/Toast';
import { toAppError } from '@repo/api-client';
import { getErrorMessage } from '@repo/i18n';

export const Route = createFileRoute('/_auth/accounting/accounts')({
  component: AccountsPage,
});

function flattenAccounts(nodes: AccountNodeData[] | undefined): AccountNodeData[] {
  if (!nodes) return [];
  const result: AccountNodeData[] = [];
  function recurse(n: AccountNodeData) {
    result.push(n);
    if (n.children) {
      (n.children as AccountNodeData[]).forEach(recurse);
    }
  }
  nodes.forEach(recurse);
  return result;
}

function AccountsPage() {
  const { data: accounts, isLoading, isError, refetch } = useAccountsTree();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Chart of Accounts"
        subtitle="Manage ledger accounts and hierarchy."
        action={<Button variant="primary" onClick={() => setIsCreateOpen(true)}>Create Account</Button>}
      />
      <div className="flex-1 w-full min-h-0">
        {(() => {
          if (isLoading) return <Skeleton rows={5} />;
          if (isError) return (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load accounts</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          );
          return (
            <div className="bg-surface-0 border border-surface-2 rounded-lg p-4">
              {accounts?.map((node) => (
                <AccountNode key={node.id} node={node} accounts={accounts} />
              ))}
            </div>
          );
        })()}
      </div>
      {isCreateOpen && (
        <CreateAccountModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          accounts={accounts}
        />
      )}
    </div>
  );
}

function AccountNode({ node, depth = 0, accounts }: { node: AccountNodeData; depth?: number; accounts: AccountNodeData[] | undefined }) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const deactivateMutation = useDeactivateAccount(node.id);
  const { toast } = useToast();

  return (
    <div className="mb-2">
      <div 
        className="flex items-center justify-between py-2 px-3 hover:bg-surface-1 rounded"
        style={{ marginLeft: `${depth * 20}px` }}
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm text-text-muted">{node.code}</span>
          <span className="font-medium">{node.name}</span>
          <span className="text-xs bg-surface-2 px-2 py-0.5 rounded-full">{node.type}</span>
          {node.isSystem && (
            <span className="text-xs bg-info-bg text-info-text px-2 py-0.5 rounded-full">System</span>
          )}
          {!node.isActive && (
            <span className="text-xs bg-surface-2 text-text-secondary px-2 py-0.5 rounded-full">Inactive</span>
          )}
        </div>
        <div className="flex gap-2">
          <Link
            to="/ledger/$accountId"
            params={{ accountId: node.id }}
            className="text-sm text-accent-600 hover:text-accent-700 hover:underline px-2 py-1"
          >
            View Ledger
          </Link>
          <Button
            variant="ghost" size="sm" className="text-accent-600 hover:text-accent-700"
            onClick={() => setIsEditOpen(true)}
          >
            Edit
          </Button>
          {!node.isSystem && node.isActive !== false && (
            <Button
              variant="ghost" size="sm" className="text-danger-600 hover:text-danger-700"
              onClick={() => setIsDeactivateOpen(true)}
            >
              Deactivate
            </Button>
          )}
        </div>
      </div>
      {node.children && (node.children as AccountNodeData[]).map((child) => (
        <AccountNode key={child.id} node={child} depth={depth + 1} accounts={accounts} />
      ))}

      {isEditOpen && (
        <EditAccountModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          node={node}
          accounts={accounts}
        />
      )}

      {isDeactivateOpen && (
        <ConfirmDialog
          open={isDeactivateOpen}
          title="Deactivate Account"
          description={`Are you sure you want to deactivate ${node.name}? This action cannot be undone.`}
          confirmLabel="Deactivate"
          loading={deactivateMutation.isPending}
          onConfirm={async () => {
            try {
              await deactivateMutation.mutateAsync();
              setIsDeactivateOpen(false);
              toast({ type: 'success', title: 'Account deactivated', description: `${node.name} has been deactivated.` });
            } catch (err) {
              console.error('Failed to deactivate account', err);
              toast({ type: 'error', title: 'Deactivation failed', description: getErrorMessage(toAppError(err)) });
            }
          }}
          onCancel={() => setIsDeactivateOpen(false)}
        />
      )}
    </div>
  );
}

function CreateAccountModal({ isOpen, onClose, accounts }: { isOpen: boolean; onClose: () => void; accounts: AccountNodeData[] | undefined }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<'asset' | 'liability' | 'equity' | 'income' | 'expense'>('asset');
  const [parentId, setParentId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateAccount();
  const flatAccounts = flattenAccounts(accounts);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createMutation.mutateAsync({
        code,
        name,
        type,
        parentId: parentId || null
      });
      onClose();
    } catch (err) {
      console.error('Failed to create account', err);
      setError(getErrorMessage(toAppError(err)));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-surface-1">
        <DialogHeader>
          <DialogTitle>Create Account</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Account Code" required htmlFor="create-account-code">
            <Input
              id="create-account-code"
              type="text"
              required
              className="font-mono"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. 1010"
            />
          </FormField>
          <FormField label="Account Name" required htmlFor="create-account-name">
            <Input
              id="create-account-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Petty Cash"
            />
          </FormField>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Account Type</label>
            <select
              className="w-full px-3 py-2 border border-surface-2 rounded-md bg-surface-0 text-text-primary text-sm"
              value={type}
              onChange={(e) => setType(e.target.value as 'asset' | 'liability' | 'equity' | 'income' | 'expense')}
            >
              <option value="asset">Asset</option>
              <option value="liability">Liability</option>
              <option value="equity">Equity</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Parent Account (Optional)</label>
            <select
              className="w-full px-3 py-2 border border-surface-2 rounded-md bg-surface-0 text-text-primary text-sm font-mono"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">None (Root Account)</option>
              {flatAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-danger-600">{error}</p>}
          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={createMutation.isPending}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditAccountModal({ isOpen, onClose, node, accounts }: { isOpen: boolean; onClose: () => void; node: AccountNodeData; accounts: AccountNodeData[] | undefined }) {
  const [name, setName] = useState(node.name);
  const [parentId, setParentId] = useState<string>(node.parentId || '');
  const [error, setError] = useState<string | null>(null);

  const editMutation = useUpdateAccount(node.id);
  const flatAccounts = flattenAccounts(accounts).filter(a => a.id !== node.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await editMutation.mutateAsync({
        name,
        parentId: parentId || null
      });
      onClose();
    } catch (err) {
      console.error('Failed to update account', err);
      setError(getErrorMessage(toAppError(err)));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-surface-1">
        <DialogHeader>
          <DialogTitle>Edit Account: {node.code}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Account Name" required htmlFor="edit-account-name">
            <Input
              id="edit-account-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormField>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Parent Account (Optional)</label>
            <select
              className="w-full px-3 py-2 border border-surface-2 rounded-md bg-surface-0 text-text-primary text-sm font-mono"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">None (Root Account)</option>
              {flatAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-danger-600">{error}</p>}
          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={editMutation.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
